import type {} from "@mui/x-data-grid-pro/themeAugmentation";

import { checkboxClasses } from "@mui/material/Checkbox";
import { createTheme } from "@mui/material/styles";

//colors new
export const COLORS = {
  neutrals: {
    ash: "#242424",
    denseFog: "#CBCBCB",
    eerieBlack: "#1A1A1A",
    fog: "#EEEEEE",
    stormyGray: "#6E6E6E",
    blueCharcoal: "#1A212B",
  },
  navy: {
    base: "#212B36",
    L10: "#2C3640",
    L20: "#424B54",
    L30: "#646B72",
    L40: "#858A90",
    L50: "#A6AAAF",
    L70: "#BCBFC3",
    L80: "#D3D5D7",
    L90: "#E9EAEB",
    L95: "#F4F4F5",
    D10: "#1F2933",
    D20: "#1C252E",
    D30: "#171E26",
    D40: "#12181E",
    D50: "#0D1116",
  },
  orange: {
    base: "#C05621",
    L10: "#C35E2C",
    L20: "#C96F42",
    L30: "#D38964",
    L40: "#DCA285",
    L50: "#E6BBA6",
    L70: "#ECCCBC",
    L80: "#F2DDD3",
    L90: "#F9EEE9",
    D10: "#B6521F",
    D20: "#A3491C",
    D30: "#863C17",
    D40: "#6A2F12",
    D50: "#4D220D",
  },
  darkOrange: {
    D20: "#85381C",
    D30: "#6D2E17",
  },
  tan: {
    base: "#F1EDE9",
    D30: "#A9A6A3",
    D40: "#858280",
    D50: "#605F5D",
  },
  red: {
    base: "#E53E3E",
    L30: "#ED7878",
    L40: "#F19595",
    L50: "#F5B2B2",
    L80: "#FAD8D8",
    D20: "#C33535",
    D30: "#A02B2B",
    D40: "#7E2222",
  },
  cobalt: {
    base: "#1177BB",
    D20: "#2B7491",
    D30: "#245F77",
    D40: "#1C485E",
    D50: "#143644",
  },
  green: {
    L40: "#92CBAC",
    L50: "#AFD9C3",
    D30: "#27714A",
    D50: "#16402A",
  },
  yellow: {
    L40: "#FAEEA6",
    L50: "#FBF3BF",
    D20: "#D1BE50",
    D40: "#877B34",
    D50: "#625A26",
  },
};

export const TOOLTIP_BG_DARK = COLORS.navy.L10;

const palette = {
  primary: { main: COLORS.orange.base, light: COLORS.orange.L80 },
  secondary: { main: COLORS.navy.base, dark: COLORS.navy.D20 },
  background: {
    // Bridged to the CSS-variable design tokens (lib/styles/mode.css) so MUI
    // surfaces follow the v2 light/dark swap, applied as raw CSS so palette.mode
    // can stay "light" while the rendered color swaps via [data-theme].
    default: "rgba(var(--background-primary))",
    paper: "rgba(var(--background-secondary))",
  },
  action: {
    // Token-driven (with an alpha) so hover feedback is visible on the dark
    // canvas; a fixed black overlay vanishes there.
    hover: "rgba(var(--foreground-primary), 0.08)",
  },
  text: {
    primary: "rgba(var(--text-primary))",
    secondary: "rgba(var(--text-secondary))",
  },
};

export const theme = createTheme({
  components: {
    // Dark-mode bridge (#3128): remaining MUI surfaces (menus, dialogs, grid
    // chrome) that are not styled via Tailwind in XGridTable.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(var(--background-secondary))",
          color: "rgba(var(--text-primary))",
          backgroundImage: "none",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          // MUI X v6 derives its inner cell/header border color from the
          // light-mode palette (lighten(divider, .88) ~ near-white), which
          // reads as harsh white lines on the dark surfaces.
          "& .MuiDataGrid-withBorderColor": {
            borderColor: "rgba(var(--outline-decorative))",
          },
          // Header sort / menu / filter glyphs default to a dark action color
          // that disappears on the dark header.
          "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-filterIcon, & .MuiDataGrid-menuIcon":
            {
              color: "rgba(var(--foreground-secondary))",
            },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          // Menu/dropdown icons (incl. the DataGrid column menu) default to
          // the dark light-mode action color.
          color: "rgba(var(--foreground-secondary))",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          // Unchecked box uses the strong foreground so its outline stays
          // legible on dark rows; the checked and indeterminate glyphs keep
          // the softer secondary tone to avoid a harsh near-white fill on dark.
          color: "rgba(var(--foreground-primary))",
          [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
            color: "rgba(var(--foreground-secondary))",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          // Filter/column panels use outlined MUI buttons internally.
          backgroundColor: "rgba(var(--background-primary))",
        },
      },
    },
  },
  palette,
  typography: {
    fontFamily: ["DM Sans Variable", "sans-serif"].join(","),
  },
});
